/**
 * Simplifies text HTML for the prediction service ML {@link https://github.com/wix-private/santa-editor/blob/199ae691c5f6fcc2edf08e230be1398245d52f26/santa-editor/packages/textClassification/prediction/fetchPrediction.ts#L4 predictor-service/v1/invoke/model/texttype-catboost-1-2}.
 *
 * Removes unneeded spans and default styles to ensure HTML structure is clean and minimal
 * for accurate predictions (prediction service returns type of text - title, subtitle, paragraph, etc. so we need to remove all the styles that are not relevant to the text type).
 * This is needed because old text components were migrated
 * to new ones, resulting in unnecessary nested spans and default styles.
 * https://github.com/wix-private/santa-editor/blob/199ae691c5f6fcc2edf08e230be1398245d52f26/santa-editor/packages/textControls/utils/migrationUtils.ts#L72
 */
export const simplifyTextHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove line-height and letter-spacing from all elements' style
  doc.querySelectorAll('[style]').forEach((el) => {
    const style = el.getAttribute('style') || '';
    const newStyle = style
      .split(';')
      .map((s) => s.trim())
      .filter(
        (s) =>
          s && !s.startsWith('line-height') && !s.startsWith('letter-spacing'),
      )
      .join('; ');
    if (newStyle) {
      el.setAttribute('style', newStyle);
    } else {
      el.removeAttribute('style');
    }
  });

  // Remove nested spans with only letter-spacing or line-height styles
  doc.querySelectorAll('span[style]').forEach((span) => {
    const style = span.getAttribute('style') || '';
    const styles = style
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);

    const onlyLetterOrLineHeight = styles.every(
      (s) => s.startsWith('letter-spacing') || s.startsWith('line-height'),
    );

    if (onlyLetterOrLineHeight) {
      const parent = span.parentNode;
      while (span.firstChild) {
        parent?.insertBefore(span.firstChild, span);
      }
      parent?.removeChild(span);
    } else {
      // Remove letter-spacing and line-height from span styles if present
      const filteredStyles = styles.filter(
        (s) => !s.startsWith('letter-spacing') && !s.startsWith('line-height'),
      );
      if (filteredStyles.length > 0) {
        span.setAttribute('style', filteredStyles.join('; '));
      } else {
        span.removeAttribute('style');
      }
    }
  });

  return doc.body.innerHTML;
};
